import { useEffect, useRef } from 'react';
import { useSetAtom, useAtomValue } from 'jotai';
import { entityCountsCacheAtom } from '~/atoms/gridData';
import { initialFilters } from '~/atoms/filters';
import { userPreferencesAtom } from '~/atoms/userPreferences';
import { lookupsAtom } from '~/atoms/lookups';
import { fetchProjects } from '~/service/projectsService';
import { fetchExpressions } from '~/service/expressionsService';
import { fetchModelsData } from '~/service/fetchModelsData';
import { fetchStylesData } from '~/service/fetchStylesData';
import { fetchProductInstances } from '~/service/productInstancesService';
import log from '~/logger';
const ENTITIES_TO_PREFETCH = [
    'project',
    'expression',
    'model',
    'style',
    'product'
];
/**
 * Prefetches entity counts for tab display.
 * Runs once when user preferences and lookups are available.
 */
export const usePrefetchEntityCounts = () => {
    const setEntityCounts = useSetAtom(entityCountsCacheAtom);
    const userPreferences = useAtomValue(userPreferencesAtom);
    const lookups = useAtomValue(lookupsAtom);
    const hasPrefetched = useRef(false);
    useEffect(() => {
        if (hasPrefetched.current)
            return;
        if (!userPreferences?.division || !lookups?.divisions)
            return;
        hasPrefetched.current = true;
        const divisionName = lookups.divisions.find((d) => String(d.id) === userPreferences.division)?.name;
        const filters = {
            ...initialFilters,
            filters: { divisions: divisionName ? [divisionName] : [] }
        };
        const prefetchAll = async () => {
            const counts = {};
            await Promise.all(ENTITIES_TO_PREFETCH.map(async (entity) => {
                try {
                    const count = await fetchEntityCount(entity, filters, userPreferences);
                    counts[entity] = count;
                    log.info(`Prefetched count for ${entity}:`, count);
                }
                catch (error) {
                    log.warn(`Failed to prefetch count for ${entity}:`, error);
                }
            }));
            if (Object.keys(counts).length > 0) {
                setEntityCounts((prev) => ({ ...prev, ...counts }));
            }
        };
        if ('requestIdleCallback' in window) {
            window.requestIdleCallback(() => prefetchAll());
        }
        else {
            setTimeout(prefetchAll, 500);
        }
    }, [userPreferences, lookups, setEntityCounts]);
};
async function fetchEntityCount(entity, filters, userPreferences) {
    switch (entity) {
        case 'project': {
            const result = await fetchProjects();
            return result.pages?.totalResources ?? 0;
        }
        case 'expression': {
            const result = await fetchExpressions({
                brandId: userPreferences.brand || undefined,
                divisionId: userPreferences.division || undefined,
                fieldOfPlayId: userPreferences.fieldsOfPlay || undefined
            });
            return result.pages?.totalResources ?? 0;
        }
        case 'model': {
            const result = await fetchModelsData(filters);
            return result.pages?.totalResources ?? 0;
        }
        case 'style': {
            const result = await fetchStylesData(filters);
            return result.pages?.totalResources ?? 0;
        }
        case 'product': {
            const result = await fetchProductInstances({ count: 1 });
            return result.pages?.totalResources ?? result.objects.length;
        }
        default:
            return 0;
    }
}
